import { useCallback, useState } from 'react';
import { supabase } from '@/shared/lib/supabase';

/**
 * useHostingSubscriptionCancel — customer-self-cancels a hosting auto-pay
 * subscription.
 *
 * Spec: _bmad-output/PHASE_PF_Q3_REVERSAL_BRD_2026-06-16.md §5.5
 *
 * EF best-effort-calls PayFast cancel API + flips local state. The
 * customer_link.recurring_enabled flag is then flipped by the
 * hosting_sub_sync_customer_link AFTER trigger.
 *
 * Cancel does NOT suspend hosting — the customer just reverts to per-invoice
 * mode. billing-cron resumes issuing renewal invoices at the next
 * customer_link.next_billing_date.
 */

export interface HostingSubscriptionCancelError {
  code: string;
  message: string;
}

export interface CancelResponse {
  ok: true;
  cancelled_at: string;
  payfast_cancel_ok?: boolean;
  idempotent?: boolean;
  note?: string;
}

export interface UseHostingSubscriptionCancelResult {
  cancel: (subscriptionId: string, reason?: string) => Promise<CancelResponse | null>;
  isLoading: boolean;
  error: HostingSubscriptionCancelError | null;
}

export function useHostingSubscriptionCancel(): UseHostingSubscriptionCancelResult {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<HostingSubscriptionCancelError | null>(null);

  const cancel = useCallback(async (subscriptionId: string, reason?: string) => {
    setIsLoading(true);
    setError(null);
    try {
      const { data, error: efError } = await supabase.functions.invoke<CancelResponse>(
        'hosting-subscription-cancel',
        { body: { hosting_subscription_id: subscriptionId, reason } }
      );

      if (efError) {
        const code = await extractErrorCode(efError);
        setError({ code, message: friendlyErrorMessage(code) });
        return null;
      }

      if (!data?.ok) {
        setError({ code: 'unknown', message: friendlyErrorMessage('unknown') });
        return null;
      }
      return data;
    } catch (err) {
      setError({ code: 'unknown', message: (err as Error).message });
      return null;
    } finally {
      setIsLoading(false);
    }
  }, []);

  return { cancel, isLoading, error };
}

async function extractErrorCode(err: unknown): Promise<string> {
  try {
    const ctx = (err as { context?: { response?: Response } }).context;
    if (ctx?.response) {
      const body = await ctx.response.clone().json();
      if (typeof body?.code === 'string') return body.code;
    }
  } catch {
    /* ignore */
  }
  return 'unknown';
}

function friendlyErrorMessage(code: string): string {
  switch (code) {
    case 'subscription_not_found':
      return 'That subscription does not exist on your account.';
    case 'forbidden_not_owner':
      return "You don't have permission to cancel this subscription.";
    case 'subscription_terminal_state':
      return 'This subscription is already in a final state.';
    case 'unauthenticated':
      return 'Please sign in again to continue.';
    default:
      return 'Could not cancel auto-pay. Please try again or contact support.';
  }
}
